import axios from './axiosInstance';

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사(쇼핑몰) 전용 통신 모듈
 * ════════════════════════════════════════════════════════════════════════════════
 *  [비토리지 일반회원 로그인과 완전히 분리하는 이유]
 *   - 비토리지 회원 토큰은 localStorage/sessionStorage 의 'AToken' 을 Authorization 헤더로 보낸다.
 *   - 상점사 토큰을 같은 방식으로 보내면 백엔드의 JwtAuthenticationFilter 가 회원으로 조회하려다
 *     403 을 반환하고, 반대로 회원 세션과 상점 세션이 서로를 덮어쓰는 사고가 난다.
 *   -> 그래서 상점사는 별도 저장키(StoreAT/StoreRT) + 별도 헤더(X-STORE-TOKEN) 를 사용한다.
 *
 *  [저장방식]
 *   스펙에 따라 '자동로그인' 체크박스나 sessionStorage 없이 localStorage 로만 로그인한다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

const STORE_AT = 'StoreAT';
const STORE_RT = 'StoreRT';
const STORE_TOKEN_HEADER = 'X-STORE-TOKEN';

/** 상점사 로그인 경로 (미인증 시 이동) */
export const STORE_LOGIN_PATH = '/store/api/login';
/** 상점사 대시보드 경로 */
export const STORE_DASHBOARD_PATH = '/store/api/dashboard';

export const getStoreToken = () => window.localStorage.getItem(STORE_AT);
export const getStoreRefreshToken = () => window.localStorage.getItem(STORE_RT);

export const setStoreTokens = (accessToken, refreshToken) => {
    if (accessToken) window.localStorage.setItem(STORE_AT, accessToken);
    if (refreshToken) window.localStorage.setItem(STORE_RT, refreshToken);
};

export const clearStoreTokens = () => {
    window.localStorage.removeItem(STORE_AT);
    window.localStorage.removeItem(STORE_RT);
};

export const isStoreLoggedIn = () => !!getStoreToken();

const storeHeaders = (extra = {}) => ({
    ...extra,
    [STORE_TOKEN_HEADER]: getStoreToken() || '',
});

/** AT 만료 시 RT로 1회 재발급 시도 */
const reissue = async () => {
    const refreshToken = getStoreRefreshToken();
    if (!refreshToken) return false;
    try {
        const res = await axios.post('/store/reissue', {refreshToken});
        if (res?.data?.accessToken) {
            setStoreTokens(res.data.accessToken, res.data.refreshToken);
            return true;
        }
    } catch (e) {
        /* 재발급 실패 */
    }
    clearStoreTokens();
    return false;
};

/**
 * 상점사 인증이 필요한 요청 공통 래퍼
 * - 401 이면 RT로 1회 재발급 후 재시도하고, 그래도 실패하면 로그인 페이지로 보낸다.
 */
export const storeRequest = async (config, options = {}) => {
    const build = () => ({
        ...config,
        headers: storeHeaders(config.headers),
    });

    try {
        return await axios(build());
    } catch (error) {
        const status = error?.response?.status;
        if (status === 401 && !options.retried) {
            const ok = await reissue();
            if (ok) return storeRequest(config, {retried: true});
            if (options.redirectOnFail !== false) {
                window.location.href = STORE_LOGIN_PATH;
            }
        }
        throw error;
    }
};

export const storeGet = (url, params, options) =>
    storeRequest({method: 'get', url, params}, options);

export const storePost = (url, data, options) =>
    storeRequest({method: 'post', url, data}, options);

/** 상점사 로그인 */
export const storeLogin = async (email, password) => {
    const res = await axios.post('/store/login', {email, password});
    setStoreTokens(res.data.accessToken, res.data.refreshToken);
    return res.data;
};

/** 상점사 로그아웃 */
export const storeLogout = () => {
    clearStoreTokens();
};
